import { Controller } from "@hotwired/stimulus"
import { Tab } from "bootstrap"

// Bootstrap tab switching for show sections: tabs react instantly, section
// content loads lazily via turbo-frame src on first visit.
//
// ←/→ changent d'onglet dès l'arrivée sur la page. Bootstrap sait déjà le faire, mais
// seulement quand un onglet a le focus — donc uniquement après un premier clic. On écoute
// donc au niveau du document et on lui délègue l'activation (`Tab#show`), ce qui garde le
// chargement paresseux du panneau et la synchro d'URL branchés sur `shown.bs.tab`.
const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]'
const TAB_SELECTOR = '[role="tab"], [data-bs-toggle="tab"]'
// Le tableau d'une section est focusable (tabindex=0) : quand on a cliqué dedans, ←/→ lui
// appartiennent — c'est la seule façon de le faire défiler au clavier sur une show.
const SCROLLER_SELECTOR = '.index-scroll-table'

// Apps natives : Android rafraîchit un écran (tirer vers le bas) en rechargeant l'adresse avec
// laquelle il a été ouvert, sans le `?section=` posé depuis par les onglets : la fiche revenait sur
// Informations. La dernière section affichée de chaque fiche est gardée pour la session de la
// WebView, et rouverte quand la page est rechargée de zéro (pas après une visite Turbo).
const NATIVE_APP = /Hotwire Native|Turbo Native/.test(navigator.userAgent)
const SECTION_STORAGE_PREFIX = "depozen.showSection:"
let loadedByTurbo = false
document.addEventListener("turbo:visit", () => {
  loadedByTurbo = true
})

const storedSection = (pathname) => {
  try {
    return sessionStorage.getItem(SECTION_STORAGE_PREFIX + pathname)
  } catch {
    return null
  }
}

const storeSection = (pathname, slug) => {
  try {
    sessionStorage.setItem(SECTION_STORAGE_PREFIX + pathname, slug)
  } catch {
    // Stockage indisponible : la fiche se rouvrira sur sa section par défaut.
  }
}

export default class extends Controller {
  connect() {
    this.onTabShown = this.onTabShown.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.syncStickyOffsets = this.syncStickyOffsets.bind(this)
    this.tabList = this.element.querySelector('[role="tablist"]')

    if (this.tabList) {
      this.tabList.addEventListener("shown.bs.tab", this.onTabShown)
      document.addEventListener("keydown", this.onKeyDown)
      this.restoreNativeSection()
    }

    // Single-section pages render no tablist but still have a lazy pane to load.
    this.loadVisibleLazyFrames()

    this.syncStickyOffsets()

    this.contextBand = this.element.querySelector(".depozen-ui-show-context-band.sticky-top")
    if (this.contextBand && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.syncStickyOffsets)
      this.resizeObserver.observe(this.contextBand)
    }
  }

  disconnect() {
    if (this.tabList) {
      this.tabList.removeEventListener("shown.bs.tab", this.onTabShown)
      document.removeEventListener("keydown", this.onKeyDown)
    }

    this.resizeObserver?.disconnect()
    this.element.style.removeProperty("--depozen-ui-show-context-band-offset")
  }

  onKeyDown(event) {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return
    if (this.isSuspended) return

    const target = event.target instanceof Element ? event.target : null
    // Un onglet a déjà le focus : Bootstrap fait le travail, on ne le double pas.
    if (target?.closest(TAB_SELECTOR)) return
    // Un champ de saisie garde ses flèches (curseur), un tableau de section aussi (défilement).
    if (target?.closest(EDITABLE_SELECTOR) || target?.closest(SCROLLER_SELECTOR)) return

    const tabs = this.navigableTabs()
    if (tabs.length < 2) return

    const current = tabs.findIndex((tab) => tab.classList.contains("active"))
    const delta = event.key === "ArrowRight" ? 1 : -1
    // Même boucle que la navigation clavier native de Bootstrap, qui cycle aux extrémités.
    const next = tabs[current === -1 ? 0 : (current + delta + tabs.length) % tabs.length]
    if (!next || next === tabs[current]) return

    event.preventDefault()
    next.focus({ preventScroll: true })
    // La barre d'onglets défile horizontalement quand il y en a beaucoup.
    next.scrollIntoView({ block: "nearest", inline: "nearest" })
    Tab.getOrCreateInstance(next).show()
  }

  navigableTabs() {
    return Array.from(this.tabList.querySelectorAll('[role="tab"]')).filter(
      (tab) => !tab.disabled && !tab.classList.contains("disabled") && tab.getClientRects().length > 0
    )
  }

  // Une couche par-dessus (modale, dropdown, palette de recherche) gère ses propres flèches.
  get isSuspended() {
    const { classList } = document.body

    return (
      classList.contains("modal-open") ||
      classList.contains("global-search-palette-open") ||
      classList.contains("global-search-inline-active") ||
      Boolean(document.querySelector(".dropdown-menu.show"))
    )
  }

  onTabShown(event) {
    const button = event.target.closest('[role="tab"]')
    if (!button) return

    const paneSelector = button.getAttribute("data-bs-target")
    const pane = paneSelector ? this.element.querySelector(paneSelector) : null
    if (!pane) return

    this.loadLazyFrame(pane)
    this.syncUrl(button.dataset.sectionSlug)
  }

  loadVisibleLazyFrames() {
    this.element.querySelectorAll(".tab-pane.active, turbo-frame.tab-pane.active").forEach((pane) => {
      this.loadLazyFrame(pane)
    })
  }

  loadLazyFrame(pane) {
    const frame = pane.tagName === "TURBO-FRAME" ? pane : pane.querySelector("turbo-frame")
    if (!frame) return

    const lazySrc = frame.dataset.lazySrc
    if (!lazySrc || frame.getAttribute("src")) return

    frame.setAttribute("src", lazySrc)
  }

  syncUrl(sectionSlug) {
    if (!sectionSlug) return

    const url = new URL(window.location.href)
    if (NATIVE_APP) storeSection(url.pathname, sectionSlug)

    if (sectionSlug === "informations") {
      url.searchParams.delete("section")
    } else {
      url.searchParams.set("section", sectionSlug)
    }

    if (this.restoringSection) {
      this.restoringSection = false
      history.replaceState(history.state, "", url)
    } else {
      history.pushState({}, "", url)
    }
  }

  // Cf. NATIVE_APP : seulement au rechargement complet d'une fiche ouverte sans `?section=`.
  restoreNativeSection() {
    if (!NATIVE_APP || loadedByTurbo) return

    const url = new URL(window.location.href)
    if (url.searchParams.has("section")) return

    const slug = storedSection(url.pathname)
    const tab = slug && this.tabList.querySelector(`[data-section-slug="${CSS.escape(slug)}"]`)
    if (!tab || tab.classList.contains("active")) return

    this.restoringSection = true
    Tab.getOrCreateInstance(tab).show()
  }

  syncStickyOffsets() {
    const band = this.contextBand || this.element.querySelector(".depozen-ui-show-context-band.sticky-top")
    if (!band) {
      this.element.style.removeProperty("--depozen-ui-show-context-band-offset")
      return
    }

    this.element.style.setProperty(
      "--depozen-ui-show-context-band-offset",
      `${Math.ceil(band.getBoundingClientRect().height)}px`
    )
  }
}
